import { getAssessmentDocumentationMode } from './performance.assessment.utils';
import { PerformanceAssessmentDocumentationMode } from '../enum/performance.assessment.enum';
import { toRecord } from './array.utils';

export interface IStudentRatableEntity {
  studentId: number;
  gradeValue?: unknown;
  performanceLevelV2Id: number | null;
}

export interface ICommonPerformanceAssessment {
  documentationMode?: PerformanceAssessmentDocumentationMode;
  performanceVisualId: number | null;
}

export interface ICommonPerformanceAssessmentEntry {
  studentEntries: readonly IStudentRatableEntity[];
  competenceObservations: readonly IStudentRatableEntity[];
  performanceAssessment: ICommonPerformanceAssessment | null;
}

export interface IPerformanceAssessmentRatingProgress {
  openCount: number;
  ratedCount: number;
  totalCount: number;
}

export function isEntryIncomplete(params: {
  studentEntries: readonly IStudentRatableEntity[];
  competenceObservations: readonly IStudentRatableEntity[];
  performanceAssessment: ICommonPerformanceAssessment | null;
  singleStudentIdOnly?: number;
}): boolean {
  for (const studentEntry of params.studentEntries) {
    if (params.singleStudentIdOnly && studentEntry.studentId !== params.singleStudentIdOnly) {
      continue;
    }

    if (!isStudentEntryFullyRated(studentEntry, params.competenceObservations, params.performanceAssessment)) {
      return true;
    }
  }
  return false;
}

export function isStudentEntryFullyRated(
  studentEntry: IStudentRatableEntity | null,
  competenceObservations: readonly IStudentRatableEntity[],
  performanceAssessment: ICommonPerformanceAssessment | null,
): boolean {
  if (!studentEntry) return false;

  const everyObservationRated = competenceObservations
    .filter((o) => o.studentId === studentEntry.studentId)
    .every((o) => o.performanceLevelV2Id != null);

  if (!hasOverallAssessmentRating(performanceAssessment)) {
    return everyObservationRated;
  }

  return (
    (getAssessmentDocumentationMode(performanceAssessment) === PerformanceAssessmentDocumentationMode.SCHOOL_GRADE
      ? studentEntry.gradeValue != null
      : studentEntry.performanceLevelV2Id != null) && everyObservationRated
  );
}

/**
 * Visual and school-grade assessments support an overall result in addition to competence observations.
 */
export function hasOverallAssessmentRating(
  performanceAssessment: ICommonPerformanceAssessment | null | undefined,
): boolean {
  return (
    getAssessmentDocumentationMode(performanceAssessment) !== PerformanceAssessmentDocumentationMode.COMPETENCE_ONLY
  );
}

export const countFullyRated = (entry?: ICommonPerformanceAssessmentEntry | null): number => {
  if (!entry) return 0;

  const observationsByStudentId: Record<number, IStudentRatableEntity[]> = toRecord(
    entry.competenceObservations,
    (obs) => obs.studentId,
    (obs, existing) => [...(existing ?? []), obs],
  );
  const useCompetenceAssessmentForRating = !hasOverallAssessmentRating(entry.performanceAssessment);

  if (useCompetenceAssessmentForRating) {
    return entry.competenceObservations.filter((obs) => obs.performanceLevelV2Id != null).length;
  }

  return entry.studentEntries.filter((studentEntry) =>
    isStudentEntryFullyRated(
      studentEntry,
      observationsByStudentId[studentEntry.studentId] ?? [],
      entry.performanceAssessment,
    ),
  ).length;
};

export const hasGradingsForStudent = (entry: ICommonPerformanceAssessmentEntry, studentId: number): boolean => {
  return (
    entry.studentEntries.some(
      (studentEntry) =>
        studentEntry.studentId === studentId &&
        (studentEntry.gradeValue != null || studentEntry.performanceLevelV2Id != null),
    ) ||
    entry.competenceObservations.some(
      (observation) => observation.studentId === studentId && observation.performanceLevelV2Id != null,
    )
  );
};

export const countTotalToRate = (entry?: ICommonPerformanceAssessmentEntry | null): number => {
  if (!entry) return 0;
  const useCompetenceAssessmentForRating = !hasOverallAssessmentRating(entry.performanceAssessment);
  const count = useCompetenceAssessmentForRating ? entry.competenceObservations.length : entry.studentEntries.length;

  return count;
};

const scopePerformanceAssessmentEntryToStudent = (
  entry: ICommonPerformanceAssessmentEntry,
  studentId: number,
): ICommonPerformanceAssessmentEntry => ({
  competenceObservations: entry.competenceObservations.filter((observation) => observation.studentId === studentId),
  performanceAssessment: entry.performanceAssessment,
  studentEntries: entry.studentEntries.filter((studentEntry) => studentEntry.studentId === studentId),
});

export const getPerformanceAssessmentRatingProgress = (
  entry: ICommonPerformanceAssessmentEntry,
  studentId?: number,
): IPerformanceAssessmentRatingProgress => {
  const scopedEntry = studentId === undefined ? entry : scopePerformanceAssessmentEntryToStudent(entry, studentId);
  const totalCount = countTotalToRate(scopedEntry);
  const ratedCount = countFullyRated(scopedEntry);

  return {
    openCount: Math.max(0, totalCount - ratedCount),
    ratedCount,
    totalCount,
  };
};
